import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { Link, useParams, Navigate } from 'react-router-dom'
import Template from '../../components/template'
import { Workflow, WorkflowConfig } from '../../lib/types'
import Loading from '../../components/loading'
import auth from '../../lib/auth'
import * as jsyaml from 'js-yaml'
import WorkflowStepCard from './workflow-step-card'
import { hasWorkflowStepAccess, findCycleStep, workflowStatusOptions } from '../util'
import { cycles, workflows } from '../../lib/api'
import Alert from '../../components/alert'
import Tag from '../../components/tag'
import ReactMarkdown from 'react-markdown'
import ErrorMsg from '../../components/error-msg'
import Progress from '../../components/progress'

export default function WorkflowOverviewPage() {
    let { id } = useParams()
    const [workflow, setWorkflow] = useState<Workflow | null>(null)
    const [config, setConfig] = useState<WorkflowConfig | null>(null)
    const [cycleId, setCycleId] = useState<number | undefined>(undefined)
    const [percentComplete, setPercentComplete] = useState<number | undefined>(undefined)
    const [completedRows, setCompletedRows] = useState<number | undefined>(undefined)
    const [totalRows, setTotalRows] = useState<number | undefined>(undefined)
    const [startDate, setStartDate] = useState<any>(null)
    const [err, setErr] = useState('')
    const [loading, setLoading] = useState(true)
    const [existingCycle, setExistingCycle] = useState(false)

    const hasCycleStep = useMemo(() => {
        if (config) {
            return findCycleStep(config)
        } else {
            return false
        }
    }, [config])

    useEffect(() => {
        workflows.get(Number(id))
            .then(setWorkflow)
            .catch((err) => setErr(err.message || err))
        sessionStorage.removeItem('page')
        sessionStorage.removeItem('viewType')
    }, [id])

    useEffect(() => {
        if (!workflow) return
        let config = jsyaml.load(workflow.config) as WorkflowConfig
        setConfig(config)
    }, [workflow])

    const getCycleInfo = useCallback(async () => {
        try {
            const {
                id: responseCyleId,
                percentComplete,
                completedRows,
                totalRows,
                startDate,
            } = await cycles.getByWorkflow(Number(id))
            setExistingCycle(true)
            setLoading(false)
            setCycleId(responseCyleId)
            setPercentComplete(percentComplete)
            setCompletedRows(completedRows)
            setTotalRows(totalRows)
            setStartDate(String(startDate).substring(0, 10))
        } catch (err) {
            if (err instanceof Error) {
                if (err.message === 'Workflow does not have an active cycle') {
                    setLoading(false)
                    setExistingCycle(false)
                } else {
                    setErr(err.message)
                }
            }
        }
    }, [id])

    useEffect(() => {
        if (!hasCycleStep) {
            return
        }
        getCycleInfo()
    }, [hasCycleStep, id, getCycleInfo])

    let ended = config && config.end && new Date(config.end) < new Date()
    let isAdmin = auth.checkRole('admin', 'workflowAdmin')
    let isLead = auth.checkRole('lead')
    const isAuthorized = isAdmin || isLead
    let userRoles = workflow ? workflow.users.filter(user => user.id === Number(auth.user?.sub)) : []
    let isReviewer = userRoles.some(item => item.role === 'reviewer')
    if (!isAdmin && !isLead && config && userRoles.length === 0) return <Navigate to='/workflows' />

    async function incrementCycle() {
        if (window.confirm('This will end the current cycle and start a new one. Are you sure?')) {
            try {
                await cycles.end(Number(id))
                await cycles.create(Number(id))
                await getCycleInfo()
            } catch (err) {
                if (err instanceof Error) setErr(err.message)
            }
        }
    }

    async function startNewCycle() {
        if (window.confirm('This will start a new cycle for the current workflow. Are you sure?')) {
            try {
                await cycles.create(Number(id))
                await getCycleInfo()
            } catch (err) {
                if (err instanceof Error) setErr(err.message)
            }
        }
    }

    return (
        <Template auth>
            <div className='container mx-auto'>
                {err && <ErrorMsg error={err} />}
                {workflow ? (
                    <div>
                        <div className='mt-10'>
                            <div className='mb-3'>
                                <Link to='/workflows'>
                                    <i className='lni lni-chevron-left' /> Back to Workflows
                                </Link>
                            </div>
                            <h1 className='inline-block'>{workflow.name}</h1>
                            <span
                                style={{
                                    backgroundColor:
                                        workflowStatusOptions[workflow.statusId]?.backgroundColor,
                                }}
                                className='ml-2 rounded-xl text-xs font-medium py-[0.2rem] px-4'
                            >
                                {workflowStatusOptions[workflow.statusId]?.label}
                            </span>
                            {!workflow.valid && (
                                <div className='bg-red-500 text-white px-2 py-3 rounded-full inline-block text-sm ml-5'>
                                    <i className='lni lni-warning' /> Invalid Config
                                </div>
                            )}
                            {(isAdmin || (isLead && workflow.statusId === 1)) && (
                                <Link
                                    className='button blue ml-5'
                                    to={`/workflows/${workflow.id}/edit`}
                                >
                                    Edit
                                </Link>
                            )}
                            {(isAuthorized || isReviewer) && (
                                <Link
                                    className='button blue ml-5'
                                    to={`/workflows/${workflow.id}/reporting`}
                                >
                                    View Reports
                                </Link>
                            )}

                            {(isAdmin || isReviewer) && (
                                <Link
                                    className='button blue ml-5'
                                    to={`/workflows/${workflow.id}/duplicates`}
                                >
                                    View Duplicates
                                </Link>
                            )}
                        </div>

                        <Alert>
                            <span className='mr-5'>
                                <i className='lni lni-user' /> Your roles within this workflow:
                            </span>
                            {isAuthorized && <Tag color='yellow'>{auth.getRole()}</Tag>}
                            {userRoles.map((item, i) => (
                                <Tag color='yellow' key={i}>
                                    {item.role}
                                </Tag>
                            ))}
                        </Alert>

                        {hasCycleStep && isAuthorized && existingCycle && (
                            <Alert>
                                {loading ? (
                                    <Loading />
                                ) : (
                                    <div>
                                        <p>Cycle Number: {cycleId}</p>
                                        <p>Start Date: {startDate}</p>
                                        <Progress percentComplete={(percentComplete || 0) * 100} completedRows={completedRows} totalRows={totalRows} />

                                        <button className='yellow' onClick={incrementCycle}>
                                            Start Next Cycle
                                        </button>
                                    </div>
                                )}
                            </Alert>
                        )}

                        {hasCycleStep && isAdmin && !existingCycle && (
                            <Alert>
                                {loading ? (
                                    <Loading />
                                ) : (
                                    <div>
                                        <h2 className='pb-2'>This workflow has no active cycle</h2>
                                        <button className='yellow' onClick={startNewCycle}>
                                            Start New Cycle
                                        </button>
                                    </div>
                                )}
                            </Alert>
                        )}

                        {config?.description && (
                            <div className='mt-5 mb-10'>
                                <ReactMarkdown>{config.description}</ReactMarkdown>
                            </div>
                        )}

                        {workflow.valid && config && (
                            <div>
                                <h2 className='my-5 text-lg'>Steps</h2>
                                {config.steps.map((step, i) => (
                                    <div key={i}>
                                        {i > 0 && (
                                            <div className='text-green-500 text-xl ml-10'>
                                                <i className='lni lni-chevron-down-circle' />
                                            </div>
                                        )}
                                        <WorkflowStepCard
                                            step={step}
                                            id={workflow!.id}
                                            idx={i}
                                            isErrorRows={workflow.isErrorRows}
                                            hasAccess={hasWorkflowStepAccess(config, i, userRoles) && !ended}
                                            disableNextTask={
                                                existingCycle &&
                                                !!percentComplete &&
                                                percentComplete >= 1
                                            }
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                    <Loading />
                )}
            </div>
        </Template>
    )
}
